import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type CSSProperties } from 'react';
import { colors, fontFamily, radius } from './tokens';

type BaseProps = {
  label?: string;
  hint?: string;
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
  wrapperStyle?: CSSProperties;
};

type InputProps = BaseProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>;
type AreaProps = BaseProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> & { multiline: true; rows?: number };

type Props = InputProps | AreaProps;

const sizes: Record<'sm' | 'md' | 'lg', { padding: string; fontSize: number }> = {
  sm: { padding: '6px 10px', fontSize: 12.5 },
  md: { padding: '9px 12px', fontSize: 13.5 },
  lg: { padding: '12px 14px', fontSize: 14.5 },
};

export const TextField = forwardRef<HTMLInputElement | HTMLTextAreaElement, Props>(function TextField(
  props,
  ref,
) {
  const {
    label,
    hint,
    invalid,
    size = 'md',
    wrapperStyle,
    ...rest
  } = props;
  const multiline = 'multiline' in props && props.multiline;
  const s = sizes[size];
  const fieldStyle: CSSProperties = {
    width: '100%',
    border: `1px solid ${invalid ? colors.bad : colors.line2}`,
    background: colors.surface,
    color: colors.ink,
    borderRadius: radius.xs,
    padding: s.padding,
    fontSize: s.fontSize,
    fontFamily: fontFamily.sans,
    outline: 'none',
    transition: 'border-color 0.12s',
    resize: multiline ? 'vertical' : 'none',
    minHeight: multiline ? 72 : undefined,
  };
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, ...wrapperStyle }}>
      {label && (
        <span
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontSize: 10,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: colors.ink3,
          }}
        >
          {label}
        </span>
      )}
      {multiline ? (
        <textarea
          ref={ref as React.Ref<HTMLTextAreaElement>}
          style={fieldStyle}
          {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <input
          ref={ref as React.Ref<HTMLInputElement>}
          style={fieldStyle}
          {...(rest as InputHTMLAttributes<HTMLInputElement>)}
        />
      )}
      {hint && (
        <span style={{ fontSize: 11.5, color: invalid ? colors.bad : colors.ink3 }}>{hint}</span>
      )}
    </label>
  );
});
